import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";

import { useSegmentAnalytics } from "./analytics/useSegmentAnalytics";

const allowedReturnToUrls = [
  "/",
  "/inactivity-logout",
  "/signup",
  "/signup?redirectTo=/triage/ib",
] as const;

const allowedReturnToExternalUrls = [
  import.meta.env.VITE_PEPPERSTONE_COM_BASE_URL,
  import.meta.env.VITE_PARTNERS_MARKETING_BASE_URL,
] as const;

type LogoutWithReturnToProps = {
  returnTo: (typeof allowedReturnToUrls)[number];
  returnToExternal?: never;
};

type LogoutWithReturnToExternalProps = {
  returnTo?: never;
  returnToExternal: (typeof allowedReturnToExternalUrls)[number];
};

type LogoutProps = LogoutWithReturnToProps | LogoutWithReturnToExternalProps;

export const useLogout = () => {
  const { logout } = useAuth0();
  const queryClient = useQueryClient();

  const { reset } = useSegmentAnalytics();

  return {
    logout: ({ returnTo, returnToExternal }: LogoutProps) => {
      queryClient.clear();
      reset();

      let logoutReturnTo: string;
      if (
        returnToExternal &&
        allowedReturnToExternalUrls.includes(returnToExternal)
      ) {
        logoutReturnTo = returnToExternal;
      } else {
        logoutReturnTo = `${window.location.origin}${returnTo ?? "/"}`;
      }

      const encodedReturnTo = encodeURIComponent(logoutReturnTo);

      return logout({
        logoutParams: {
          returnTo: `${window.location.origin}/logout/callback?returnTo=${encodedReturnTo}`,
        },
      });
    },
  };
};
